import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Moeda } from '../../../../../../enums/moeda.enum';
import { environment } from '../../../../../../environments/environment';
import { MoedaDTO } from '../../../../../../models/configuracoes/empresa.model';
import { ItemDTO } from '../../../../../../models/item.model';
import {
  ItemDeNotaDTO,
  ItemDeNotaFormDTO,
} from '../../../../../../models/nota.model';
import { EmpresaConfigService } from '../../../../../../services/configuracoes/empresa.service';
import { ItemsService } from '../../../../../../services/financeiro/item.service';
import { SafeStorageService } from '../../../../../../shared/safe-storage.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-produto-selecionar',
  templateUrl: './produto-selecionar.component.html',
  styleUrls: ['./produto-selecionar.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class SelecionarItemComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('buscaInput') buscaInput!: ElementRef;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() pessoaId: number | undefined;

  env = environment;

  itemSelected: ItemDTO = {} as ItemDTO;

  @Input() itemDeNota: ItemDeNotaDTO | undefined;
  @Output() selectedItem = new EventEmitter<ItemDeNotaFormDTO>();
  @Output() updatedItem = new EventEmitter<ItemDeNotaFormDTO>();
  itens: ItemDTO[] = [];
  first3: number = 0;
  rows3: number = 10;
  totalRecords: number = 0;
  lastfield = 'id';
  lastSort = 'asc';
  form: ItemDeNotaFormDTO = {
    itemDeNotaDTO: {
      quantidade: 0,
      valorTotal: 0,
      valorUnitario: 0,
      novoPrecoVenda: 0,
      desconto: 0,
      tipoDeImposto: undefined,
      custoUnitario: 0,
      custoTotal: 0
    }
  }
  loadingProdutos: boolean = false;
  idEmpresa: string = '';
  isEditMode = false;
  moedaPadrao: MoedaDTO | undefined;

  // Tabela de busca
  modoTabelaBusca: boolean = false;
  mostrarTabelaBusca: boolean = false;
  itemSelecionadoTabela: ItemDTO | null = null;
  buscaGlobal: string = '';
  paginaAtual: number = 0;
  opcoesLinhasPorPagina = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  private buscaSubject = new Subject<void>();

  // --- Dialog de criacao rapida (app-item-criar-rapido) ---
  // O formulario, as permissoes dos combos e o POST /item vivem no componente
  // compartilhado; aqui resta apenas a visibilidade e a regra de COMPRA.
  mostrarDialogCriarItem: boolean = false;
  casasDecimaisQuantidade: number = 0;

  // Tabela canonica de simbolos por moeda (compartilhada entre o getter
  // moedaPadraoSimbolo e o helper formatarValorMonetario).
  private readonly SIMBOLOS_MOEDA: Record<Moeda, string> = {
    [Moeda.GUARANI]: 'Gs',
    [Moeda.REAL]: 'R$',
    [Moeda.DOLAR]: '$',
    [Moeda.PESO]: '$',
  };

  @HostListener('document:keydown', ['$event'])
  onDialogKeydown(ev: KeyboardEvent): void {
    // Escape: se tabela de busca esta aberta, fechar apenas a tabela
    if (ev.key === 'Escape' && this.mostrarTabelaBusca) {
      ev.preventDefault();
      ev.stopPropagation();
      this.fecharTabelaBusca();
      return;
    }

    if (ev.ctrlKey && ev.key === 'Enter') {
      ev.preventDefault();
      if (this.isEditMode) {
        this.atualizar();
      } else {
        this.selecionar();
      }
      return;
    }
  }

  constructor(
    private itemService: ItemsService,
    private empresaConfigService: EmpresaConfigService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private safe: SafeStorageService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.idEmpresa = this.safe.getItem('id') || '';
    this.isEditMode = !!this.itemDeNota;

    this.carregarMoedaPadrao();

    // Debounce para buscas
    this.buscaSubject.pipe(debounceTime(400)).subscribe(() => {
      this.paginaAtual = 0;
      this.buscarItens();
    });

    // Se é modo edicao, carregar itens e preparar edicao
    // Se nao, iniciar no formulario vazio (sem mostrar tabela)
    if (this.itemDeNota) {
      this.buscarItens(() => {
        this.prepararEdicao();
      });
    }
  }

  ngAfterViewInit() {
    // Focus no input de busca apenas quando a tabela estiver visivel
    if (this.mostrarTabelaBusca) {
      setTimeout(() => {
        this.buscaInput?.nativeElement?.focus();
      }, 200);
    }
  }

  ngOnDestroy() {
    this.buscaSubject.complete();
  }

  prepararEdicao() {
    if (!this.itemDeNota) return;

    if (this.itemDeNota.item) {
      this.itemSelected = this.itemDeNota.item;
    } else if (this.itemDeNota.item_id) {
      const foundItem = this.itens.find(i => i.id === this.itemDeNota?.item_id);
      if (foundItem) {
        this.itemSelected = foundItem;
      }
    }

    this.form.item = this.itemSelected;
    this.form.itemDeNotaDTO = { ...this.itemDeNota };

    // Atualizar casas decimais da quantidade baseado na unidade de medida do item
    this.casasDecimaisQuantidade = (this.itemSelected as any).unidadeMedidaDeItem?.casasDecimais ?? 0;
  }

  // --- Busca na tabela ---

  onBuscaGlobalChange() {
    this.buscaSubject.next();
  }

  customSort(event: any): void {
    let sort = 'desc';
    if (event.order === 1) {
      sort = 'asc';
    }

    if (this.lastfield === event.field && this.lastSort === sort) {
      return;
    }

    this.lastfield = String(event.field);
    this.lastSort = sort;
    this.paginaAtual = 0;
    this.buscarItens();
  }

  onLinhasPorPaginaChange() {
    this.paginaAtual = 0;
    this.buscarItens();
  }

  irParaPagina(pagina: number) {
    if (pagina < 0 || pagina >= this.totalPaginas) return;
    this.paginaAtual = pagina;
    this.buscarItens();
  }

  get totalPaginas(): number {
    return Math.ceil(this.totalRecords / this.rows3) || 1;
  }

  get firstRecord(): number {
    return this.totalRecords === 0 ? 0 : this.paginaAtual * this.rows3 + 1;
  }

  get lastRecord(): number {
    const last = (this.paginaAtual + 1) * this.rows3;
    return last > this.totalRecords ? this.totalRecords : last;
  }

  buscarItens(callback?: () => void) {
    this.loadingProdutos = true;
    const temBuscaGlobal = this.buscaGlobal?.trim();

    if (!temBuscaGlobal) {
      // Sem filtros - busca todos
      this.itemService.get('/item', {
        page: this.paginaAtual,
        size: this.rows3,
        sort: `${this.lastfield},${this.lastSort}`
      }).subscribe(
        (response) => {
          this.itensMap(response.content);
          this.totalRecords = response.totalElements;
          this.loadingProdutos = false;
          if (callback) callback();
        },
        (error) => {
          console.error('Erro ao buscar itens:', error);
          this.itens = [];
          this.totalRecords = 0;
          this.loadingProdutos = false;
        }
      );
    } else {
      // Com filtros - busca unificada por nome, barras, código ou código fabricante
      const texto = this.buscaGlobal.trim();

      this.itemService.get('/item/buscar', {
        page: this.paginaAtual,
        size: this.rows3,
        sort: `${this.lastfield},${this.lastSort}`,
        filter: `nome=${texto}&barras=${texto}&codigoItem=${texto}&codigoFabricante=${texto}&nomeGrupo=${texto}&nomeSubGrupo=${texto}`,
      }).subscribe(
        (response) => {
          this.itensMap(response.content);
          this.totalRecords = response.totalElements;
          this.loadingProdutos = false;
          if (callback) callback();
        },
        (error) => {
          console.error('Erro ao buscar itens:', error);
          this.itens = [];
          this.totalRecords = 0;
          this.loadingProdutos = false;
        }
      );
    }
  }

  // --- Seleção na tabela ---

  onRowSelect(event: any) {
    this.itemSelecionadoTabela = event.data;
  }

  onRowDblClick(produto: ItemDTO) {
    this.itemSelected = produto;
    this.form.item = produto;

    // Apenas campos informativos de referencia recebem valores do produto
    // Quantidade, valorTotal e valorUnitario iniciam zerados para o usuario preencher
    this.form.itemDeNotaDTO.custoUnitario = produto.custo || 0;
    this.form.itemDeNotaDTO.custoTotal = produto.custo || 0;
    this.form.itemDeNotaDTO.quantidade = 0;
    this.form.itemDeNotaDTO.valorUnitario = 0;
    this.form.itemDeNotaDTO.valorTotal = 0;
    this.form.itemDeNotaDTO.novoPrecoVenda = produto.precoDeVenda || 0;

    // Atualizar casas decimais da quantidade baseado na unidade de medida do item
    this.casasDecimaisQuantidade = (produto as any).unidadeMedidaDeItem?.casasDecimais ?? 0;

    // Voltar ao formulario (nao emitir/fechar mais)
    this.mostrarTabelaBusca = false;
    this.modoTabelaBusca = false;
  }

  confirmarSelecaoTabela() {
    if (!this.itemSelecionadoTabela) return;
    this.itemSelected = this.itemSelecionadoTabela;
    this.form.item = this.itemSelected;

    // Apenas campos informativos de referencia recebem valores do produto
    // Quantidade, valorTotal e valorUnitario iniciam zerados para o usuario preencher
    this.form.itemDeNotaDTO.custoUnitario = this.itemSelected.custo || 0;
    this.form.itemDeNotaDTO.custoTotal = this.itemSelected.custo || 0;
    this.form.itemDeNotaDTO.quantidade = 0;
    this.form.itemDeNotaDTO.valorUnitario = 0;
    this.form.itemDeNotaDTO.valorTotal = 0;
    this.form.itemDeNotaDTO.novoPrecoVenda = this.itemSelected.precoDeVenda || 0;

    // Atualizar casas decimais da quantidade baseado na unidade de medida do item
    this.casasDecimaisQuantidade = (this.itemSelected as any).unidadeMedidaDeItem?.casasDecimais ?? 0;

    this.modoTabelaBusca = false;
    this.mostrarTabelaBusca = false;
  }

  voltarParaTabela() {
    this.itemSelected = {} as ItemDTO;
    this.form.item = undefined;
    this.modoTabelaBusca = false;
    this.mostrarTabelaBusca = false;
    this.itemSelecionadoTabela = null;
  }

  /** Abre a tabela de busca de produtos (chamado pela lupa) */
  abrirTabelaBusca() {
    this.mostrarTabelaBusca = true;
    this.modoTabelaBusca = false;
    this.itemSelecionadoTabela = null;

    // Carregar itens se ainda nao foram carregados
    if (this.itens.length === 0) {
      this.buscarItens();
    }

    // Focus no input de busca apos abrir
    setTimeout(() => {
      this.buscaInput?.nativeElement?.focus();
    }, 200);
  }

  /** Fecha a tabela de busca e volta ao formulario */
  fecharTabelaBusca() {
    this.mostrarTabelaBusca = false;
  }

  // --- Moeda ---

  carregarMoedaPadrao(): void {
    if (!this.idEmpresa) return;

    this.empresaConfigService.get(`/configuracaoEmpresa/${this.idEmpresa}`).subscribe({
      next: (res) => {
        this.moedaPadrao = res?.moeda;
      },
      error: (err) => {
        console.error('Erro ao buscar configuração da empresa:', err);
      },
    });
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/images/profile_placeholder.png';
  }

  itensMap(itens: ItemDTO[]) {
    this.itens = itens.map((item) => ({
      ...item,
      formatted: `(${item.barras}) - ${item.nome}`,
    }));
  }

  get valorTotal() {
    this.form.itemDeNotaDTO.valorUnitario =
      this.form.itemDeNotaDTO.valorTotal / this.form.itemDeNotaDTO.quantidade;
    return this.form.itemDeNotaDTO.valorUnitario || 0;
  }

  openProdutos() {
    window.open('/produtos', '_blank');
  }

  selecionar() {
    if (!this.form.item) {
      this.confirmDialog('Atenção', 'Selecione um item para adicionar á nota');
      return;
    }

    const item = this.form.item;
    const dto = this.form.itemDeNotaDTO;

    const itemDeNota: ItemDeNotaFormDTO = {
      item: item,
      itemDeNotaDTO: {
        item_id: item.id,
        item: item,
        quantidade: dto.quantidade,
        custoUnitario: this.valorTotal != 0 ? this.valorTotal : item.custo,
        custoTotal: dto.custoTotal,
        valorUnitario: this.valorTotal,
        valorTotal: dto.valorTotal,
        desconto: dto.desconto,
        tipoDeImposto: dto.tipoDeImposto,
        novoPrecoVenda: dto.novoPrecoVenda != 0 ? dto.novoPrecoVenda : item.precoDeVenda,
      }
    };

    this.selectedItem.emit(itemDeNota);
    this.closeDialog();
  }

  atualizar() {
    if (!this.form.item) {
      this.confirmDialog('Atenção', 'Selecione um item para atualizar');
      return;
    }

    const item = this.form.item;
    const dto = this.form.itemDeNotaDTO;

    const itemAtualizado: ItemDeNotaFormDTO = {
      itemDeNotaDTO: {
        item_id: item.id,
        item: item,
        quantidade: dto.quantidade,
        custoUnitario: this.valorTotal != 0 ? this.valorTotal : item.custo,
        custoTotal: dto.custoTotal,
        valorUnitario: this.valorTotal,
        valorTotal: dto.valorTotal,
        desconto: dto.desconto,
        tipoDeImposto: dto.tipoDeImposto,
        novoPrecoVenda: dto.novoPrecoVenda != 0 ? dto.novoPrecoVenda : item.precoDeVenda,
      },
      item: this.form.item,
    };

    this.updatedItem.emit(itemAtualizado);
    this.closeDialog();
  }

  closeDialog() {
    this.visibleChange.emit(false);
  }

  confirmDialog(header: string, message: string) {
    this.confirmationService.confirm({
      message: message,
      header: header,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        return true;
      },
    });
  }

  // --- Criacao rapida de item ---

  /**
   * Abre o dialog compartilhado `app-item-criar-rapido`.
   *
   * A pre-condicao "escolha o fornecedor primeiro" e regra da COMPRA e fica
   * AQUI de proposito: o dialog compartilhado trata `fornecedorId` como
   * opcional porque a VENDA (Operar Cliente) nao tem fornecedor. Mover esta
   * guarda para dentro dele bloquearia a venda.
   */
  abrirDialogCriarItem(): void {
    if (!this.pessoaId) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('produto-selecionar.messages.atencao'),
        detail: this.translate.instant('produto-selecionar.messages.selecione_fornecedor_primeiro')
      });
      return;
    }

    // O reset do formulario e a pre-selecao da unidade padrao acontecem dentro
    // do proprio dialog, no flanco de subida do `visible`.
    this.mostrarDialogCriarItem = true;
  }

  /**
   * `(criado)` do dialog compartilhado: auto-seleciona o item recem-criado no
   * formulario de compra. Comportamento identico ao que existia dentro do
   * antigo `salvarNovoItem()` (o POST, o toast e o fechamento agora sao do
   * componente compartilhado).
   */
  onNovoItemCriado(itemCriado: ItemDTO): void {
    this.itemSelected = itemCriado;
    this.form.item = itemCriado;
    this.form.itemDeNotaDTO.custoUnitario = itemCriado.custo || 0;
    this.form.itemDeNotaDTO.custoTotal = itemCriado.custo || 0;
    this.form.itemDeNotaDTO.quantidade = 0;
    this.form.itemDeNotaDTO.valorUnitario = 0;
    this.form.itemDeNotaDTO.valorTotal = 0;
    this.form.itemDeNotaDTO.novoPrecoVenda = itemCriado.precoDeVenda || 0;

    // Casas decimais da quantidade seguem a unidade de medida do item criado.
    this.casasDecimaisQuantidade = (itemCriado as any).unidadeMedidaDeItem?.casasDecimais ?? 0;

    // Garantir que a tabela de busca esta fechada (o usuario volta ao form).
    this.mostrarTabelaBusca = false;
    this.modoTabelaBusca = false;
  }

  private obterNomeMoeda(moeda: any): string | null {
    return moeda?.enum ?? null;
  }

  formatarValorMonetario(valor: number, moeda?: MoedaDTO): string {
    if (valor == null || isNaN(valor)) return '-';

    if (!moeda) {
      moeda = this.moedaPadrao;
    }

    const localesMoeda: Record<Moeda, string> = {
      [Moeda.GUARANI]: 'es-PY',
      [Moeda.REAL]: 'pt-BR',
      [Moeda.DOLAR]: 'en-US',
      [Moeda.PESO]: 'es-AR',
    };

    const nomeMoeda = this.obterNomeMoeda(moeda) as Moeda | null;
    const moedaKey = nomeMoeda ?? Moeda.GUARANI;
    const locale = localesMoeda[moedaKey];
    const simbolo = this.SIMBOLOS_MOEDA[moedaKey];

    const isGuarani = nomeMoeda === Moeda.GUARANI || moedaKey === Moeda.GUARANI;

    const options: Intl.NumberFormatOptions = isGuarani
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

    const valorFormatado = new Intl.NumberFormat(locale, options).format(valor);

    return `${simbolo} ${valorFormatado}`;
  }

  /**
   * Simbolo da moeda padrao da empresa (Gs, R$, $).
   * Retorna '' se a moeda ainda nao foi carregada — sem fallback,
   * para nao exibir simbolo potencialmente incorreto (RF05).
   */
  get moedaPadraoSimbolo(): string {
    const nomeMoeda = this.obterNomeMoeda(this.moedaPadrao) as Moeda | null;
    if (!nomeMoeda) return '';
    return this.SIMBOLOS_MOEDA[nomeMoeda] || '';
  }

  get locale(): string {
    return this.moedaPadrao?.pais?.local?.replaceAll('_', '-') || 'pt-BR';
  }

  get currencyCode(): string {
    return this.moedaPadrao?.codigoOperacao || 'BRL';
  }

  get casasDecimaisDaMoeda(): number {
    return this.moedaPadrao?.casasDecimais ?? 0;
  }
}
